import { useState, useEffect, useMemo } from 'react'
import {
  ArrowRightIcon,
  CalendarIcon,
  ClockIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { UserProfile } from '../types/user'
import { useAuth } from '../hooks/useAuth'
import { useUserProfile } from '../hooks/useUserProfile'
import { getUserMeetings } from '../lib/meetingService'
import { initiateStripeConnectOnboarding, isProfessionalBookingEligible } from '../lib/professionalService'
import { getUserReviewSummary, ReviewSummary } from '../lib/reviewService'
import { MeetingListItem } from '../types/meeting'
import { getUserTimezone, convertAvailabilityFromGMT } from '../utils/timezoneUtils'
import { Browser } from '@capacitor/browser'
import { isNativePlatform } from '../utils/platform'

interface DashboardProps {
  onNavigate: (view: string) => void
  userProfile?: UserProfile | null
}

export default function Dashboard({ onNavigate, userProfile: propUserProfile }: DashboardProps) {
  const { user: authUser } = useAuth()
  const { userProfile, loading: profileLoading } = useUserProfile()
  const [upcomingMeetings, setUpcomingMeetings] = useState<MeetingListItem[]>([])
  const [pastMeetings, setPastMeetings] = useState<MeetingListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isConnectingStripe, setIsConnectingStripe] = useState(false)
  const [weekOffset, setWeekOffset] = useState(0)
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null)

  // Use hook data or prop fallback
  const profile = userProfile || propUserProfile
  const user = profile ? {
    firstName: profile.firstName || profile.displayName?.split(' ')[0] || 'User',
    name: profile.firstName || profile.displayName || 'User',
    email: profile.email || 'user@example.com',
    accountType: profile.userType === 'professional' ? 'Professional' : 'Client',
    timezone: profile.timezone,
    title: profile.title || '',
    bio: profile.bio || '',
    specializations: profile.skills || (profile.specialization ? [profile.specialization] : []),
    sessionRate: profile.sessionRate,
    profileImage: profile.profileImage || '',
    company: profile.company || ''
  } : {
    firstName: 'User',
    name: 'User',
    email: 'user@example.com',
    accountType: 'Client',
    timezone: undefined,
    title: '',
    bio: '',
    specializations: [] as string[],
    sessionRate: undefined as number | undefined,
    profileImage: '',
    company: ''
  }

  // Fetch real meeting data
  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!authUser) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const userTimezone = user.timezone ? getUserTimezone(user.timezone) : undefined
        const allMeetings = await getUserMeetings(authUser.uid, undefined, userTimezone)

        const upcoming: MeetingListItem[] = []
        const past: MeetingListItem[] = []

        allMeetings.forEach(meeting => {
          const timeBasedStatus = getTimeBasedStatus(meeting)
          if (timeBasedStatus === 'scheduled' || timeBasedStatus === 'in_progress') {
            upcoming.push(meeting)
          } else {
            past.push(meeting)
          }
        })
        setUpcomingMeetings(upcoming)
        setPastMeetings(past)
      } catch {
        // Error fetching meeting data
      } finally {
        setLoading(false)
      }
    }

    if (!profileLoading) {
      fetchMeetingData()
    }
  }, [authUser, user.timezone, profileLoading])

  // Fetch review summary for professional's public card
  useEffect(() => {
    if (authUser && user.accountType === 'Professional') {
      getUserReviewSummary(authUser.uid).then(setReviewSummary).catch(() => setReviewSummary(null))
    }
  }, [authUser, user.accountType])

  // Handle Stripe connect
  const handleStripeConnect = async () => {
    if (!authUser || !userProfile) return

    setIsConnectingStripe(true)
    try {
      const result = await initiateStripeConnectOnboarding(authUser.uid, {
        baseUrl: window.location.origin
      })

      if (result.url) {
        if (isNativePlatform()) {
          await Browser.open({ url: result.url })
        } else {
          window.open(result.url, '_blank')
        }
      }
    } catch {
      // Error connecting Stripe
    } finally {
      setIsConnectingStripe(false)
    }
  }

  // Helper function to determine real-time meeting status
  const getTimeBasedStatus = (meeting: MeetingListItem) => {
    const currentTime = new Date()
    const meetingDate = new Date(meeting.datetime)
    const meetingEndTime = new Date(meetingDate.getTime() + (meeting.duration * 60 * 1000))

    if (meeting.status === 'cancelled' || meeting.status === 'completed' || meeting.status === 'no_show') {
      return meeting.status
    }

    if (currentTime < meetingDate) {
      return 'scheduled'
    } else if (currentTime >= meetingDate && currentTime <= meetingEndTime) {
      return 'in_progress'
    } else {
      return 'completed'
    }
  }

  // Setup progress calculation
  const isStripeSetup = userProfile ? isProfessionalBookingEligible({
    id: userProfile.uid,
    stripeAccountId: userProfile.stripeAccountId,
    isStripeOnboarded: userProfile.isStripeOnboarded
  } as any) : false

  const hasBasics = !!(user.firstName && user.firstName !== 'User' && (user.title || user.company))
  const isPublished = !!profile?.profileCompleted && isStripeSetup

  const setupSteps = [
    { label: 'Create your account', sublabel: 'Done', done: true },
    { label: 'Add the basics', sublabel: 'Name, role, company', done: hasBasics },
    { label: 'Connect Stripe for payouts', sublabel: 'Required to accept paid bookings', done: isStripeSetup },
    { label: 'Publish your profile', sublabel: isStripeSetup ? 'Ready to publish' : 'Unlocks after Stripe is connected', done: isPublished }
  ]

  const completedSteps = setupSteps.filter(s => s.done).length
  const progressPercent = Math.round((completedSteps / setupSteps.length) * 100)
  const stepsRemaining = setupSteps.length - completedSteps
  const needsSetup = user.accountType === 'Professional' && completedSteps < setupSteps.length

  // Get the active step (first incomplete step)
  const activeStepIndex = setupSteps.findIndex(s => !s.done)

  // Parse user's availability into local time
  const localAvailability = useMemo(() => {
    if (!profile?.availability) return null
    try {
      const tz = getUserTimezone(profile.timezone)
      return convertAvailabilityFromGMT(profile.availability as any, tz)
    } catch {
      return null
    }
  }, [profile])

  // Map day index (0=Mon) to availability key
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

  // Week day strip calculation
  const weekDays = useMemo(() => {
    const today = new Date()
    const startOfWeek = new Date(today)
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)) // Monday start

    const days = []
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek)
      date.setDate(startOfWeek.getDate() + i)
      const isToday = date.toDateString() === today.toDateString()

      // Check if any meetings fall on this day
      const dayMeetings = upcomingMeetings.filter(m => {
        const meetingDate = new Date(m.datetime)
        return meetingDate.toDateString() === date.toDateString()
      })

      // Check user's availability for this day of week
      const dayKey = dayKeys[i]
      const dayAvail = localAvailability ? (localAvailability as any)[dayKey] : null
      const isAvailable = dayAvail?.enabled && dayAvail?.timeSlots?.length > 0
      const isOff = localAvailability ? !isAvailable : i >= 5 // fallback to weekend if no availability set

      days.push({
        name: dayNames[i],
        date: date.getDate(),
        isToday,
        isOff,
        meetingCount: dayMeetings.length,
        fullDate: date
      })
    }
    return days
  }, [weekOffset, upcomingMeetings, localAvailability])

  const weekRangeLabel = useMemo(() => {
    if (weekDays.length < 7) return ''
    const start = weekDays[0].fullDate
    const end = weekDays[6].fullDate
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${months[start.getMonth()]} ${start.getDate()} — ${months[end.getMonth()]} ${end.getDate()}`
  }, [weekDays])

  // Get today's day name
  const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' })

  // Generate real activity data
  const generateRecentActivity = () => {
    const activity: Array<{
      id: string
      title: string
      description: string
      timestamp: string
      urgent?: boolean
      action?: () => void
      actionText?: string
    }> = []

    // Add urgent Stripe setup notification for professionals
    if (user.accountType === 'Professional' && !isStripeSetup) {
      activity.push({
        id: 'stripe-setup',
        title: 'Payment setup required',
        description: 'Connect Stripe Connect to receive bookings and payouts.',
        timestamp: 'Action Required',
        urgent: true,
        action: handleStripeConnect,
        actionText: 'Set up Stripe'
      })
    }

    // Add upcoming meetings
    upcomingMeetings.slice(0, 2).forEach(meeting => {
      activity.push({
        id: `upcoming-${meeting.id}`,
        title: `Upcoming · ${meeting.name}`,
        description: `${meeting.location || meeting.type || 'Engineer'} · ${meeting.date}`,
        timestamp: meeting.time
      })
    })

    // Add completed meetings
    pastMeetings.slice(0, 2).forEach(meeting => {
      const isCancelled = meeting.status === 'cancelled'
      activity.push({
        id: `past-${meeting.id}`,
        title: `${isCancelled ? 'Cancelled' : 'Completed'} · ${meeting.name}`,
        description: `${meeting.location || meeting.type || 'Engineer'} · ${meeting.date}`,
        timestamp: meeting.date
      })
    })

    // Welcome message if empty
    if (activity.length === 0 || (activity.length === 1 && !isStripeSetup)) {
      activity.push({
        id: 'welcome',
        title: 'Welcome to Wonderstood',
        description: user.accountType === 'Professional'
          ? 'Complete your profile to start accepting bookings.'
          : 'Browse engineers and book your first session.',
        timestamp: 'Just now'
      })
    }

    return activity.slice(0, 5)
  }

  const realActivity = generateRecentActivity()


  // Show loading state
  if (loading || profileLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/40"></div>
        <span className="ml-3 text-white/50">Loading dashboard...</span>
      </div>
    )
  }

  // SVG ring calculations
  const radius = 42
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference

  return (
    <div className="max-w-[1280px]">
      {/* ===== Welcome header ===== */}
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-[44px] leading-[1.05] font-medium tracking-tight-1">
            Welcome back, <span className="font-serif italic font-normal">{user.firstName}</span>
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            {todayName} &middot;{' '}
            {needsSetup ? (
              <>You're <span className="text-white/90">{stepsRemaining} step{stepsRemaining !== 1 ? 's' : ''} away</span> from accepting your first booking.</>
            ) : (
              "Here's what's happening this week."
            )}
          </p>
        </div>
      </header>

      {/* ===== Setup hero (professionals who haven't finished setup) ===== */}
      {needsSetup && (
        <section className="panel relative overflow-hidden mb-6">
          <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none"></div>
          <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full pointer-events-none"
               style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08), transparent 60%)' }}></div>

          <div className="relative grid lg:grid-cols-[1.2fr_1fr] gap-10 p-6 sm:p-8">
            {/* LEFT: messaging + CTA */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="text-[11px] tracking-wide-3 uppercase text-white/50">Get set up</div>
                <span className="text-white/20">&middot;</span>
                <div className="text-xs text-white/50">{completedSteps} of {setupSteps.length} complete</div>
              </div>
              <h2 className="text-2xl sm:text-[34px] leading-[1.1] font-medium tracking-tight-1 max-w-[28ch]">
                You're almost ready to <span className="font-serif italic font-normal">get paid</span> for what you know.
              </h2>
              <p className="text-white/50 mt-4 max-w-[52ch] text-sm">
                {!isStripeSetup
                  ? 'Connect Stripe to receive payouts, then publish your profile to start showing up in search. Most engineers finish setup in under five minutes.'
                  : 'Publish your profile to start showing up in search results. Students are already looking for engineers like you.'
                }
              </p>

              <div className="flex items-center gap-3 mt-7">
                {!isStripeSetup ? (
                  <button
                    onClick={handleStripeConnect}
                    disabled={isConnectingStripe}
                    className="btn btn-primary"
                  >
                    {isConnectingStripe ? 'Connecting...' : 'Connect Stripe'}
                    <ArrowRightIcon className="w-3.5 h-3.5" />
                  </button>
                ) : !isPublished ? (
                  <button onClick={() => onNavigate('User Profile')} className="btn btn-primary">
                    Publish profile
                    <ArrowRightIcon className="w-3.5 h-3.5" />
                  </button>
                ) : null}
                <button onClick={() => onNavigate('User Profile')} className="btn btn-ghost">
                  Edit profile
                </button>
              </div>
            </div>

            {/* RIGHT: progress ring + checklist */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-5">
                <svg width="92" height="92" viewBox="0 0 100 100" className="-rotate-90 shrink-0">
                  <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="6" className="ring-track" />
                  <circle cx="50" cy="50" r={radius} fill="none" strokeWidth="6" className="ring-fill"
                          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round"/>
                </svg>
                <div>
                  <div className="text-[34px] font-medium leading-none tracking-tight-1">
                    {progressPercent}<span className="text-white/50 text-lg">%</span>
                  </div>
                  <div className="text-white/50 text-[13px] mt-1">Profile setup</div>
                </div>
              </div>

              <div className="panel-soft px-5">
                {setupSteps.map((step, idx) => {
                  const isDone = step.done
                  const isActive = idx === activeStepIndex
                  const isLocked = idx > activeStepIndex && !isDone

                  return (
                    <div key={idx} className="step-row">
                      <span className={`step-num ${isDone ? 'step-done' : isActive ? 'step-active' : ''}`}>
                        {isDone ? (
                          <CheckIcon className="w-3 h-3" strokeWidth={3} />
                        ) : (
                          <span>{idx + 1}</span>
                        )}
                      </span>
                      <div>
                        <div className={`text-[13.5px] font-medium ${isLocked ? 'text-white/50' : ''}`}>
                          {step.label}
                        </div>
                        <div className={`text-xs ${isLocked ? 'text-white/30' : 'text-white/50'}`}>
                          {step.sublabel}
                        </div>
                      </div>
                      <span className="text-white/40 text-xs">
                        {isDone ? '—' : isActive ? (
                          <button
                            onClick={idx === 2 ? handleStripeConnect : () => onNavigate('User Profile')}
                            className="btn btn-outline !py-1.5 !px-3 text-xs"
                          >
                            Set up
                          </button>
                        ) : 'Locked'}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== Two-column: schedule + profile preview ===== */}
      <section className="grid lg:grid-cols-[1.45fr_1fr] gap-6 mb-6">

        {/* Your week panel */}
        <div className="panel p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-medium tracking-tight-half">Your week</h3>
              <p className="text-white/50 text-xs mt-1">{weekRangeLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setWeekOffset(w => w - 1)} className="btn btn-ghost !py-1.5 !px-2.5">
                <ChevronLeftIcon className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setWeekOffset(w => w + 1)} className="btn btn-ghost !py-1.5 !px-2.5">
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>
              {user.accountType === 'Professional' && (
                <button onClick={() => onNavigate('User Profile:availability')} className="btn btn-outline !py-1.5 !px-3 text-xs hidden sm:inline-flex">
                  Manage availability
                </button>
              )}
            </div>
          </div>

          {/* Day strip */}
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-4">
            {weekDays.map((day) => (
              <div
                key={`${day.name}-${day.date}`}
                className={`text-center py-3 rounded-lg ${
                  day.isToday
                    ? 'panel-soft'
                    : ''
                }`}
                style={!day.isToday ? { boxShadow: 'inset 0 0 0 1px hsl(var(--border) / 0.5)' } : undefined}
              >
                <div className="text-white/50 text-[10px] tracking-wide-3 uppercase">{day.name}</div>
                <div className={`text-lg sm:text-xl font-medium mt-1 ${day.isToday ? 'text-white' : ''}`}>{day.date}</div>
                <div className="text-white/40 text-[10.5px] mt-2">
                  {day.meetingCount > 0 ? `${day.meetingCount} session${day.meetingCount > 1 ? 's' : ''}` : day.isOff ? 'off' : '—'}
                </div>
              </div>
            ))}
          </div>

          {/* Upcoming sessions or empty state */}
          {upcomingMeetings.length > 0 ? (
            <div className="space-y-2">
              {upcomingMeetings.slice(0, 3).map((meeting) => {
                const timeStatus = getTimeBasedStatus(meeting)
                const isInProgress = timeStatus === 'in_progress'

                return (
                  <div
                    key={meeting.id}
                    onClick={() => onNavigate('Meetings')}
                    className={`rounded-xl panel-soft p-4 flex items-center gap-4 cursor-pointer transition-all hover:bg-white/[0.04] ${
                      isInProgress ? 'border-white/20' : ''
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                         style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                      {meeting.imageUrl ? (
                        <img src={meeting.imageUrl} alt="" className="w-10 h-10 rounded-full" />
                      ) : (
                        <span className="text-xs font-medium text-white/60">
                          {meeting.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{meeting.name}</span>
                        {isInProgress && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white text-black font-semibold">Live</span>
                        )}
                      </div>
                      <div className="text-white/40 text-xs mt-0.5">
                        {meeting.type || 'Session'} &middot; {meeting.duration} min
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-medium text-white/80">{meeting.time}</div>
                      <div className="text-xs text-white/40">{meeting.date}</div>
                    </div>
                  </div>
                )
              })}
              {upcomingMeetings.length > 3 && (
                <button onClick={() => onNavigate('Meetings')} className="btn btn-ghost w-full justify-center text-xs mt-2">
                  View all {upcomingMeetings.length} sessions
                  <ArrowRightIcon className="w-3 h-3" />
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-xl panel-soft p-5 flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                   style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                <ClockIcon className="w-5 h-5 text-white/60" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">No bookings yet &mdash; your calendar is wide open.</div>
                <div className="text-white/50 text-xs mt-1">
                  {user.accountType === 'Professional'
                    ? 'Once published, sessions you accept will land here with one-click join links.'
                    : 'Book a session with an engineer and it will appear here.'
                  }
                </div>
              </div>
              <button
                onClick={() => onNavigate(user.accountType === 'Professional' ? 'User Profile:availability' : 'Find Engineers')}
                className="btn btn-outline hidden sm:inline-flex"
              >
                {user.accountType === 'Professional' ? 'Set hours' : 'Browse'}
              </button>
            </div>
          )}

          {/* Tips row (only for professionals who are still setting up) */}
          {needsSetup && (
            <div className="grid sm:grid-cols-3 gap-3 mt-5">
              <div className="rounded-xl p-4" style={{ boxShadow: 'inset 0 0 0 1px hsl(var(--border) / 0.5)' }}>
                <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-2">Tip 01</div>
                <div className="text-[13.5px] font-medium leading-snug">Add three specific topics you can speak to &mdash; niche beats broad.</div>
              </div>
              <div className="rounded-xl p-4" style={{ boxShadow: 'inset 0 0 0 1px hsl(var(--border) / 0.5)' }}>
                <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-2">Tip 02</div>
                <div className="text-[13.5px] font-medium leading-snug">Engineers who add a 60-second intro video are booked 3&times; more often.</div>
              </div>
              <div className="rounded-xl p-4" style={{ boxShadow: 'inset 0 0 0 1px hsl(var(--border) / 0.5)' }}>
                <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-2">Tip 03</div>
                <div className="text-[13.5px] font-medium leading-snug">Open up two consistent weekly slots &mdash; predictability earns repeat clients.</div>
              </div>
            </div>
          )}
        </div>

        {/* Profile preview (professionals only) */}
        {user.accountType === 'Professional' ? (
          <div className="panel p-6 flex flex-col">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-medium tracking-tight-half">Your public card</h3>
              <span className="text-[10.5px] tracking-wide-3 uppercase text-white/50 inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: isPublished ? '#4ade80' : '#d4b94a' }}></span>
                {isPublished ? 'Live' : 'Unpublished'}
              </span>
            </div>

            {/* Card preview — matches Find Engineers card style */}
            <div className="eng-card">
              {/* Photo area */}
              <div className="eng-photo">
                <div className="dot-grid" style={{ position: 'absolute', inset: 0, opacity: 0.35 }}></div>
                {user.profileImage ? (
                  <img src={user.profileImage} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 1 }} />
                ) : (
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 150" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ color: 'hsl(0 0% 65% / 0.45)' }}>
                    <circle cx="100" cy="60" r="22"/>
                    <path d="M52 138c4-22 24-34 48-34s44 12 48 34"/>
                  </svg>
                )}
                {/* Rate badge */}
                <span className="badge-rate">
                  {user.sessionRate ? `$${user.sessionRate}` : '—'} <span className="opacity-70 font-medium ml-0.5">/ session</span>
                </span>
              </div>

              {/* Card body */}
              <div className="flex-1 flex flex-col p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[16.5px] font-semibold tracking-tight-half truncate">{user.name} {profile?.lastName || ''}</div>
                    <div className="text-white/50 text-[12.5px] mt-0.5 truncate">
                      {(user.title || 'Engineer').replace(' Engineer', '').replace(' Developer', '')}{user.company && <> · <span className="text-white/85">{user.company}</span></>}
                    </div>
                  </div>
                  {profile?.experience && (
                    <span className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium text-white bg-white/[0.06]"
                      style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                      {profile.experience}
                    </span>
                  )}
                </div>

                <p className="text-white/80 text-[13px] leading-relaxed mt-3 line-clamp-3">
                  {user.bio || <span className="text-white/40 italic">Add a bio so clients know what you can help them with.</span>}
                </p>

                <div className="flex items-center gap-3 mt-4 text-[11.5px] text-white/50">
                  <span className="inline-flex items-center gap-1.5">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
                    30 min
                  </span>
                  <span className="opacity-40">·</span>
                  {reviewSummary && reviewSummary.totalReviews > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" className="text-white/85"><path d="M12 2l2.95 6.6 7.05.6-5.4 4.7 1.7 7-6.3-3.8L5.7 21l1.7-7L2 9.2l7.05-.6z"/></svg>
                      {reviewSummary.averageRating.toFixed(1)} · {reviewSummary.totalReviews} review{reviewSummary.totalReviews !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="italic">No reviews yet</span>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-5">
                  <button onClick={() => onNavigate('User Profile')} className="btn btn-primary flex-1 justify-center">
                    Edit profile
                  </button>
                  <button onClick={() => onNavigate('User Profile')} className="btn btn-outline !px-3.5" aria-label="Edit card">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* What clients will see checklist */}
            <div className="mt-5 pt-5" style={{ borderTop: '1px solid hsl(var(--border) / 0.5)' }}>
              <div className="text-[11px] tracking-wide-3 uppercase text-white/50 mb-3">What clients will see</div>
              <ul className="text-xs text-white/70 space-y-2.5">
                <li className="flex items-start gap-2">
                  <CheckIcon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${hasBasics ? 'text-white' : 'text-white/30'}`} strokeWidth={2} />
                  <span className={hasBasics ? '' : 'text-white/40'}>Your name, photo, and headline at the top of search results</span>
                </li>
                <li className="flex items-start gap-2">
                  {isStripeSetup ? (
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-white" strokeWidth={2} />
                  ) : (
                    <div className="w-3.5 h-3.5 mt-0.5 shrink-0 rounded-full border border-white/30"></div>
                  )}
                  <span className={isStripeSetup ? '' : 'text-white/40'}>Session pricing &mdash; connect Stripe to enable</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          /* Quick actions for clients */
          <div className="panel p-6">
            <h3 className="text-lg font-medium tracking-tight-half mb-5">Quick actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => onNavigate('Find Engineers')}
                className="w-full rounded-xl panel-soft p-4 flex items-center gap-4 text-left transition-all hover:bg-white/[0.04]"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-white/70">
                    <circle cx="9" cy="8" r="3.2"/><circle cx="17" cy="9.5" r="2.4"/>
                    <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5"/><path d="M14.5 19c0-2 2-3.5 4-3.5s3.5 1.5 3.5 3.5"/>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium">Find engineers</div>
                  <div className="text-xs text-white/40 mt-0.5">Browse engineers by company, role, or expertise</div>
                </div>
              </button>
              <button
                onClick={() => onNavigate('Meetings')}
                className="w-full rounded-xl panel-soft p-4 flex items-center gap-4 text-left transition-all hover:bg-white/[0.04]"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                  <CalendarIcon className="w-5 h-5 text-white/70" />
                </div>
                <div>
                  <div className="text-sm font-medium">View meetings</div>
                  <div className="text-xs text-white/40 mt-0.5">See your scheduled and past sessions</div>
                </div>
              </button>
              <button
                onClick={() => onNavigate('Favorites')}
                className="w-full rounded-xl panel-soft p-4 flex items-center gap-4 text-left transition-all hover:bg-white/[0.04]"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                     style={{ background: 'rgba(255,255,255,0.04)', boxShadow: 'inset 0 0 0 1px hsl(var(--border))' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-white/70">
                    <path d="M12 21s-7-4.5-9.5-9C.8 8.5 3 5 6.5 5c2 0 3.5 1 5.5 3.2C14 6 15.5 5 17.5 5 21 5 23.2 8.5 21.5 12 19 16.5 12 21 12 21z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-medium">Favorites</div>
                  <div className="text-xs text-white/40 mt-0.5">Engineers you've saved for later</div>
                </div>
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ===== Activity feed ===== */}
      <section className="panel p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-medium tracking-tight-half">Recent activity</h3>
          <button onClick={() => onNavigate('Meetings')} className="btn btn-ghost text-xs">
            View all
            <ArrowRightIcon className="w-3 h-3" />
          </button>
        </div>

        {realActivity.length > 0 ? (
          <ul className="space-y-1">
            {realActivity.map((activity, idx) => (
              <li
                key={activity.id}
                className="grid grid-cols-[14px_1fr_auto] gap-4 items-start py-3"
                style={idx !== realActivity.length - 1 ? { borderBottom: '1px solid hsl(var(--border) / 0.4)' } : undefined}
              >
                <span
                  className="w-2 h-2 rounded-full mt-2"
                  style={{ background: activity.urgent ? '#d4b94a' : 'hsl(var(--foreground) / 0.5)' }}
                ></span>
                <div>
                  <div className="text-sm font-medium">{activity.title}</div>
                  <div className="text-white/50 text-xs mt-0.5">{activity.description}</div>
                </div>
                {activity.action && activity.actionText ? (
                  <button onClick={activity.action} className="btn btn-outline !py-1.5 !px-3 text-xs">
                    {activity.actionText}
                  </button>
                ) : (
                  <span className="text-white/40 text-xs mt-2">{activity.timestamp}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-10">
            <p className="text-white/40 text-sm">No recent activity to display</p>
            <p className="text-xs text-white/25 mt-1">
              Your bookings, ratings, and payments will appear here
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
